import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import "./Credit.css";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "spent", label: "Token Spent" },
  { value: "received", label: "Token Received" },
];

interface CreditFilterDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: {
    fromDate?: string;
    toDate?: string;
    isCreditSpent?: boolean;
    isCreditReceived?: boolean;
  }) => void;
  initialFromDate?: string;
  initialToDate?: string;
  initialStatus?: string;
  resultCount?: number;
}

const CreditFilterDropdown: React.FC<CreditFilterDropdownProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  initialFromDate = "",
  initialToDate = "",
  initialStatus = "",
  resultCount,
}) => {
  const [status, setStatus] = useState<string>(initialStatus || "");

  useEffect(() => {
    setStatus(initialStatus || "");
  }, [initialStatus]);

  // The date range is owned by the calendar in the toolbar, so carry it through
  // untouched and only change the credit status here.
  const applyStatus = (nextStatus: string) => {
    setStatus(nextStatus);
    onApplyFilters({
      fromDate: initialFromDate,
      toDate: initialToDate,
      isCreditSpent: nextStatus === "spent" ? true : undefined,
      isCreditReceived: nextStatus === "received" ? true : undefined,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full z-50 mt-1.5 w-[210px] bg-white rounded-xl shadow-xl border border-gray-200/60 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
      {/* Body */}
      <div className="px-4 pt-3 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          {resultCount !== undefined ? (
            <p className="text-[#717D96] text-xs font-normal">
              Showing {resultCount} {resultCount === 1 ? "result" : "results"}
            </p>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => applyStatus("")}
            className="text-[#717D96] hover:text-[#111827] transition-colors p-0.5 rounded-full focus:outline-none"
            title="Reset filters"
            aria-label="Reset filters"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {STATUS_OPTIONS.map((o) => {
            const isSelected = status === o.value;
            return (
              <button
                key={o.value || "all"}
                type="button"
                onClick={() => applyStatus(o.value)}
                className="flex items-center gap-2 py-0.5 text-left group cursor-pointer transition-colors"
              >
                <span
                  className={`flex-shrink-0 w-[18px] h-[18px] rounded-full border-[1.8px] flex items-center justify-center transition-all bg-white ${
                    isSelected
                      ? "border-black"
                      : "border-[#9CA3AF] group-hover:border-gray-500"
                  }`}
                >
                  {isSelected && (
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FFBC00]" />
                  )}
                </span>
                <span
                  className={`text-sm transition-colors ${
                    isSelected
                      ? "text-black font-bold"
                      : "text-[#111827] font-semibold group-hover:text-black"
                  }`}
                >
                  {o.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CreditFilterDropdown;
